import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Award, TrendingUp, Users, GraduationCap, Calendar, Download, ChevronRight, Building2, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';

interface CertificateStats {
  totalCertificates: number;
  thisMonth: number;
  byTemplate: Array<{ template: string; count: number }>;
  byCourse: Array<{ course: string; count: number; hours: number }>;
  bySchool: Array<{ school: string; count: number; users: number }>;
  byUser: Array<{ user: string; email: string; count: number; courses: string[] }>;
  monthlyTrend: Array<{ month: string; count: number }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

export function CertificateReports() {
  const [stats, setStats] = useState<CertificateStats>({
    totalCertificates: 0,
    thisMonth: 0,
    byTemplate: [],
    byCourse: [],
    bySchool: [],
    byUser: [],
    monthlyTrend: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState<'district' | 'school' | 'user'>('district');
  const [selectedSchool, setSelectedSchool] = useState<string>('');

  useEffect(() => {
    loadCertificateStats();
  }, [selectedView, selectedSchool]);

  const loadCertificateStats = async () => {
    try {
      setLoading(true);
      
      // Mock data for certificate statistics
      // In a real implementation, this would query the issued_certificates table
      const mockStats: CertificateStats = {
        totalCertificates: 234,
        thisMonth: 45,
        byTemplate: [
          { template: 'Professional Certificate - Blue', count: 89 },
          { template: 'Elegant Certificate - Gold', count: 67 },
          { template: 'Modern Certificate - Green', count: 78 }
        ],
        byCourse: [
          { course: 'Advanced Teaching Methods', count: 45, hours: 20 },
          { course: 'Classroom Management', count: 38, hours: 15 },
          { course: 'Educational Technology', count: 42, hours: 25 },
          { course: 'Student Assessment', count: 29, hours: 12 },
          { course: 'Special Education Basics', count: 34, hours: 18 }
        ],
        bySchool: [
          { school: 'Lincoln Elementary', count: 45, users: 12 },
          { school: 'Washington High School', count: 38, users: 15 },
          { school: 'Roosevelt Middle School', count: 29, users: 8 },
          { school: 'Jefferson Elementary', count: 34, users: 10 }
        ],
        byUser: [
          { user: 'Sarah Johnson', email: 'sarah.johnson@school.edu', count: 3, courses: ['Advanced Teaching Methods', 'Classroom Management', 'Educational Technology'] },
          { user: 'Mike Chen', email: 'mike.chen@school.edu', count: 2, courses: ['Student Assessment', 'Special Education Basics'] },
          { user: 'Emily Davis', email: 'emily.davis@school.edu', count: 4, courses: ['Educational Technology', 'Advanced Teaching Methods', 'Classroom Management', 'Student Assessment'] }
        ],
        monthlyTrend: [
          { month: 'Jul 24', count: 32 },
          { month: 'Aug 24', count: 41 },
          { month: 'Sep 24', count: 38 },
          { month: 'Oct 24', count: 45 },
          { month: 'Nov 24', count: 52 },
          { month: 'Dec 24', count: 45 }
        ]
      };

      setStats(mockStats);
    } catch (error) {
      console.error('Error loading certificate stats:', error);
      toast.error('Failed to load certificate statistics');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const csvData = stats.byUser.map(user => ({
      Name: user.user,
      Email: user.email,
      'Certificates Earned': user.count,
      'Courses Completed': user.courses.join('; ')
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'certificate-report.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading certificate reports...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Certificates</p>
                <p className="text-2xl font-bold">{stats.totalCertificates}</p>
              </div>
              <Award className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold">{stats.thisMonth}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Users</p>
                <p className="text-2xl font-bold">{stats.byUser.length}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Schools</p>
                <p className="text-2xl font-bold">{stats.bySchool.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Controls */}
      <div className="flex items-center gap-4">
        <Select value={selectedView} onValueChange={(value: any) => setSelectedView(value)}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="district">District Overview</SelectItem>
            <SelectItem value="school">School Breakdown</SelectItem>
            <SelectItem value="user">User Details</SelectItem>
          </SelectContent>
        </Select>
        
        <Button onClick={exportToCSV} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Certificate Issuance Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={{}}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.monthlyTrend}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <ChartTooltip />
                  <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Templates Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>By Template Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={{}}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats.byTemplate}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    label={({ template, percent }) => `${template} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {stats.byTemplate.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Drill-down Views */}
      {selectedView === 'school' && (
        <Card>
          <CardHeader>
            <CardTitle>Certificates by School</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.bySchool.map((school, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{school.school}</p>
                      <p className="text-sm text-muted-foreground">{school.users} active users</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{school.count} certificates</Badge>
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedView === 'user' && (
        <Card>
          <CardHeader>
            <CardTitle>Certificates by User</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.byUser.map((user, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>
                        {user.user.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{user.user}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Courses: {user.courses.slice(0, 2).join(', ')}
                        {user.courses.length > 2 && ` +${user.courses.length - 2} more`}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">{user.count} certificates</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedView === 'district' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Courses */}
          <Card>
            <CardHeader>
              <CardTitle>Top Courses by Certificates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats.byCourse.map((course, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">{course.course}</p>
                        <p className="text-xs text-muted-foreground">{course.hours} hours</p>
                      </div>
                    </div>
                    <Badge variant="outline">{course.count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Course Hours Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Certificates by Course Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={{}}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.byCourse}>
                    <XAxis 
                      dataKey="course" 
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      fontSize={10}
                    />
                    <YAxis />
                    <ChartTooltip />
                    <Bar dataKey="count" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}